/**
 * Visual juice for the timing games (owner decision, 2026-08-18: "particles on
 * click, particles on end, cool screen flashes AND MOVEMENTS... we need
 * vibes!!!"). Pure DOM + CSS: each burst spawns a handful of short-lived
 * absolutely-positioned particles whose trajectory is handed to CSS through
 * `--dx`/`--dy` custom properties, and the screen flash is a single fading
 * full-viewport overlay. Everything self-removes, adds no libraries, and is
 * fully skipped when the viewer prefers reduced motion.
 */

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined') return false;
  if (document.documentElement.dataset.reducedMotion === 'true') return true;
  return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
}

let overlay: HTMLDivElement | null = null;
function getOverlay(): HTMLDivElement {
  if (overlay && document.body.contains(overlay)) return overlay;
  overlay = document.createElement('div');
  overlay.className = 'fx-overlay';
  overlay.setAttribute('aria-hidden', 'true');
  document.body.appendChild(overlay);
  return overlay;
}

export interface BurstOptions {
  /** How many particles. Kept modest so a burst never janks a mid-game frame. */
  count?: number;
  /** CSS colours the particles are randomly drawn from. */
  colors?: string[];
  /** Rough pixel radius the particles fly out to. */
  spread?: number;
  /** Particle edge size in px. */
  size?: number;
}

/**
 * Fires a radial particle burst centred on `origin` (an element or an explicit
 * point). Used on press (a small spark off the button) and on release (a
 * bigger, tier-coloured pop).
 */
export function burstParticles(origin: HTMLElement | { x: number; y: number }, options: BurstOptions = {}): void {
  if (prefersReducedMotion()) return;
  const { count = 14, colors = ['#ff5c35', '#ffd24a', '#ffffff'], spread = 90, size = 7 } = options;

  let cx: number;
  let cy: number;
  if (origin instanceof HTMLElement) {
    const rect = origin.getBoundingClientRect();
    cx = rect.left + rect.width / 2;
    cy = rect.top + rect.height / 2;
  } else {
    cx = origin.x;
    cy = origin.y;
  }

  const root = getOverlay();
  const burst = document.createElement('div');
  burst.className = 'fx-burst';
  burst.style.left = `${cx}px`;
  burst.style.top = `${cy}px`;

  for (let i = 0; i < count; i += 1) {
    const angle = (Math.PI * 2 * i) / count + Math.random() * 0.5;
    const distance = spread * (0.55 + Math.random() * 0.75);
    const particle = document.createElement('span');
    particle.className = 'fx-particle';
    particle.style.setProperty('--dx', `${Math.cos(angle) * distance}px`);
    particle.style.setProperty('--dy', `${Math.sin(angle) * distance}px`);
    particle.style.setProperty('--rot', `${Math.random() * 360}deg`);
    particle.style.width = `${size}px`;
    particle.style.height = `${size}px`;
    particle.style.background = colors[i % colors.length];
    burst.appendChild(particle);
  }

  root.appendChild(burst);
  window.setTimeout(() => burst.remove(), 900);
}

/**
 * A brief full-viewport colour flash — the release "impact". `intensity` is the
 * peak opacity (0-1); a good hit flashes brighter than a poor one.
 */
export function screenFlash(color: string, intensity = 0.28): void {
  if (prefersReducedMotion()) return;
  const root = getOverlay();
  const flash = document.createElement('div');
  flash.className = 'fx-flash';
  flash.style.background = `radial-gradient(circle at center, ${color} 0%, transparent 70%)`;
  flash.style.setProperty('--flash-peak', String(intensity));
  root.appendChild(flash);
  window.setTimeout(() => flash.remove(), 520);
}

/**
 * A short shake nudge on an element — the release "movement" (owner:
 * "MOVEMENTS"). Applies a class that runs a keyframe, then cleans it up so it
 * can be retriggered next round.
 */
export function shake(el: HTMLElement | null, variant: 'soft' | 'hard' = 'soft'): void {
  if (!el || prefersReducedMotion()) return;
  const cls = variant === 'hard' ? 'fx-shake--hard' : 'fx-shake';
  el.classList.remove('fx-shake', 'fx-shake--hard');
  // force reflow so the animation restarts even on a back-to-back trigger
  void el.offsetWidth;
  el.classList.add(cls);
  window.setTimeout(() => el.classList.remove(cls), 500);
}
